import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Card, Stack, Container, Typography, TablePagination, IconButton, Tooltip } from '@mui/material';
import Page from '../../components/Page';
import useQuery from '../../hooks/useQuery';
import { useApi } from '../../hooks/useApi';
import { ListToolbar } from '../../components/tables';
import tracker from '../../api/tracker';
import FreeSelect from '../../components/hook-form/FreeSelect';
import SortDropdown from '../../components/SortDropdown';
import useNotification from '../../hooks/useNotificaction';
import AgGridWrapper, { agDefaultColDef, StatusCell } from '../../components/tables/AgGridWrapper';
import RescheduleDialog from './RescheduleDialog';

const override = { margin: '0 auto' };

export default function MeetingIndex() {
  const [meetings, setMeetings] = useState([]);
  const [sendNotification] = useNotification();
  const [pagination, setPagination] = useState({ total: 0, per_page: 10, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [filterScope, setFilterScope] = useState('all');
  const sort = 'desc';
  const sortField = 'created_at';
  const [isLoading, setIsLoading] = useState(true);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [meetingToReschedule, setMeetingToReschedule] = useState(null);
  const { doSave } = useApi();

  const fetchData = useQuery();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const filterOptions = [
    { value: 'all', label: t('all') },
    { value: 'forStatus(pending)', label: t('pending') },
    { value: 'forStatus(accepted)', label: t('accepted') },
    { value: 'forStatus(rejected)', label: t('rejected') },
  ];

  const statusOptions = [
    { value: 'accepted', label: t('accepted') },
    { value: 'pending', label: t('pending') },
    { value: 'rejected', label: t('rejected') },
    { value: 'cancelled', label: t('cancelled') },
  ];

  const refreshTable = async () => {
    setIsLoading(true);
    await fetchData('meetings', filter, sort, setMeetings, sortField, setPagination, page, () => {}, filterScope);
    setIsLoading(false);
  };

  useEffect(() => {
    const delay = setTimeout(refreshTable, 500);
    return () => clearTimeout(delay);
  }, [filter, sort, page, sortField, filterScope]);

  const onStatusChange = async (status, meetingHash) => {
    await tracker.post(`meetings/${meetingHash}/setStatus`, { status, method: '_PUT' });
    sendNotification({ msg: t('status_updated'), variant: 'success' });
    refreshTable();
  };

  const onReschedule = async (dates) => {
    await doSave('meetings', dates, t('meeting_rescheduled_success'), null, meetingToReschedule, {}, '', '/reschedule');
    setShowRescheduleDialog(false);
    setMeetingToReschedule(null);
    refreshTable();
  };

  const columnDefs = [
    { field: 'fullname', headerName: t('name'), flex: 1, minWidth: 150, filter: 'agTextColumnFilter' },
    { field: 'date', headerName: t('date'), flex: 1, minWidth: 130 },
    { field: 'email', headerName: t('email'), flex: 1, minWidth: 180, filter: 'agTextColumnFilter' },
    { field: 'phone', headerName: t('phone'), flex: 1, minWidth: 130 },
    {
      field: 'status',
      headerName: t('status'),
      flex: 1,
      minWidth: 120,
      cellRenderer: (params) => <StatusCell value={params.value} t={t} />,
    },
    {
      field: 'motive',
      headerName: t('motive'),
      flex: 1.5,
      minWidth: 220,
      cellRenderer: (params) => {
        const value = params.value || '';
        if (!value) return null;
        return (
          <Tooltip
            title={value}
            arrow
            placement="top-start"
            enterDelay={250}
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 420,
                  fontSize: 13,
                  lineHeight: 1.45,
                  whiteSpace: 'pre-wrap',
                  p: 1.25,
                },
              },
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                width: '100%',
                cursor: 'help',
              }}
            >
              {value}
            </span>
          </Tooltip>
        );
      },
    },
    {
      headerName: t('edit_status'),
      width: 170,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <FreeSelect
          initialValue={params.data.status}
          onChange={(e) => onStatusChange(e.target.value, params.data.hashid)}
          options={statusOptions}
        />
      ),
    },
    {
      headerName: '',
      width: 100,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={t('view')}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => navigate(`/dashboard/meeting/view/${params.data.hashid}`)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reschedule')}>
            <IconButton
              size="small"
              onClick={() => {
                setMeetingToReschedule(params.data);
                setShowRescheduleDialog(true);
              }}
            >
              <EventAvailableIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Page title={t('meetings')}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="flex-start" mb={5}>
          <Typography variant="h4" gutterBottom>
            {t('meetings')}
          </Typography>
        </Stack>

        <Card>
          <Stack direction="row" alignItems="center" mb={1}>
            <ListToolbar
              numSelected={0}
              filterName={filter}
              onFilterName={(e) => setFilter(e.target.value)}
              placeholder={t('search_meetings')}
            />
            <SortDropdown sx={{ width: '200px' }} options={filterOptions} sort={filterScope} setSort={setFilterScope} />
          </Stack>

          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <FadeLoader color="blue" loading cssOverride={override} size={50} />
            </div>
          ) : (
            <AgGridWrapper rowData={meetings} columnDefs={columnDefs} defaultColDef={agDefaultColDef} />
          )}

          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page || 10}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
          />
        </Card>

        {meetingToReschedule && (
          <RescheduleDialog
            open={showRescheduleDialog}
            meeting={meetingToReschedule}
            onClose={() => {
              setShowRescheduleDialog(false);
              setMeetingToReschedule(null);
            }}
            onConfirm={onReschedule}
          />
        )}
      </Container>
    </Page>
  );
}
